import { useCallback, useEffect, useState } from 'react';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { getLatestAvailablePage } from '@Utils/getLatestAvailablePage';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import {
  HrmRequestFormResponse,
  ITRequestFormResponse,
  MaterialRequisitionFormResponse,
  PurchaseFormResponse,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { SortConfig } from '@Features/types/SortConfig';
import { SearchCriteria } from '@Types/api/common/filters';
import { useGetAttendanceRequestsMutation } from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/api/attendanceRequestApiSlice';
import { useGetITRequestsMutation } from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import { useGetLeaveRequestsMutation } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/leaveRequestApiSlice';
import { useGetPassportRequestsMutation } from '@Features/EmployeeSelfService/requests/PassportRequest/store/api/passportRequestApiSlice';
import { useGetSalaryCertificatesMutation } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/api/salaryCertificateApiSlice';
import { useGetResumptionRequestsMutation } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/api/resumptionRequestApiSlice';
import { useGetOverseasRequestsMutation } from '@Features/EmployeeSelfService/requests/OverseasMission/store/api/overseasRequestApiSlice';
import { useGetResignationRequestsMutation } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';
import { useGetMaterialRequestsMutation } from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';
import { useGetSearchListPurchaseRequestsMutation } from '@Features/MaterialRequest/requests/PurchaseRequest/store/api/purchaseRequestApiSlice';
import { useGetProfileAttendancesMutation } from '@Store/api/profileApiSlice';
import { useSearchTenderMutation } from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import {
  useGetApprovalTaskListMutation,
  useGetApprovalTaskListRequisitionMutation,
} from '@Store/api/approvalsListApiSlice';
import { TenderDTO } from '@Types/api/form/tenderRequest/TenderDTO';
import {
  ApprovalsTasksResponse,
  ApprovalsTasksRequisitionResponse,
} from '@Types/api/approvalsList';

import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { EventMessageTypes } from '@Enum/EventMessageTypes.enum';
import { filterDuplicateObjects } from '@Utils/filterDuplicateObjects';

import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { eventMessageType } from '@Store/selectors/eventMessageTypeSelector';
import RequestType from '@Types/common/RequestTypes';
import { RequestType as RequestTypeEnum } from '@Enum/index';
import { currentFilterSettingsSelector } from '@Store/selectors/filterOptionsSelector';
import { AttendanceProfileResponse } from '@Types/api/profile/AttendanceProfileList';

import { useAppDispatch, useAppSelector, useResizeLayout } from '.';

type RequestResponseSearchListTypes =
  | HrmRequestFormResponse
  | ITRequestFormResponse
  | ResignationRequestFormResponse
  | MaterialRequisitionFormResponse
  | PurchaseFormResponse
  | TenderDTO
  | AttendanceProfileResponse
  | ApprovalsTasksResponse
  | ApprovalsTasksRequisitionResponse;

type RequestBodyType = RequestListSearchRequest & {
  services: Array<'ESS' | 'IT' | 'RESIGNITION' | 'MR'>;
};

interface RequestRefetcherProps {
  requestType: RequestType;
  searchValue: string | undefined;
  filters: SearchCriteria[] | undefined;
  sorting: SortConfig | null;
  isRequestFormPage: boolean;
  isRequestFormPagePrev: React.MutableRefObject<boolean>;
  clearParams: () => void;
  services?: Array<'ESS' | 'IT' | 'RESIGNITION' | 'MR'>;
}

interface ReturnValue {
  currentPage: number;
  isSuccess: boolean;
  isLoading: boolean;
  listRequests:
    | RequestListSearchResponse<RequestResponseSearchListTypes>
    | undefined;
  requestsDataList: Array<RequestResponseSearchListTypes>;
  fetchRequest: (changedPage?: number, sortConfig?: SortConfig | null) => void;
  handleLoadMore: () => void;
  onChangeCurrentPage: (value: number) => void;
}

const mapperRequests = {
  Leave: useGetLeaveRequestsMutation,
  Passport: useGetPassportRequestsMutation,
  Salary: useGetSalaryCertificatesMutation,
  ITRequisition: useGetITRequestsMutation,
  Material: useGetMaterialRequestsMutation,
  Purchase: useGetSearchListPurchaseRequestsMutation,
  Overseas: useGetOverseasRequestsMutation,
  Resumption: useGetResumptionRequestsMutation,
  Attendance: useGetAttendanceRequestsMutation,
  AttendanceProfile: useGetProfileAttendancesMutation,
  Resignation: useGetResignationRequestsMutation,
  Tender: useSearchTenderMutation,
  ESSTaskRequest: useGetApprovalTaskListMutation,
  MRTaskRequest: useGetApprovalTaskListRequisitionMutation,
};

const useRequestRefetcher = ({
  requestType,
  searchValue,
  filters,
  sorting,
  isRequestFormPage,
  isRequestFormPagePrev,
  clearParams,
  services = ['ESS', 'IT', 'RESIGNITION'],
}: RequestRefetcherProps): ReturnValue => {
  const [getRequests, { isSuccess, isLoading }] = mapperRequests[requestType]();
  const [currentPage, setCurrentPage] = useState(TABLE_PAGE_DEFAULT);
  const [listRequests, setListRequests] =
    useState<RequestListSearchResponse<RequestResponseSearchListTypes>>();
  const [requestsDataList, setRequestsDataList] = useState<
    Array<RequestResponseSearchListTypes>
  >([]);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { isMobile } = useResizeLayout();
  const eventMessage = useAppSelector(eventMessageType);
  const filterSettings = useAppSelector(currentFilterSettingsSelector);

  const handleLoadMore = useCallback(
    (
      nextPage?: number,
      clearRequestDataList?: boolean,
      sortConfig?: SortConfig | null
    ): void => {
      if (loading) {
        return;
      }
      setLoading(true);

      const sortingValue = sortConfig || sorting;
      const counterPage = nextPage || 0;

      let bodyRequest = {
        searchValue,
        filters: filterSettings,
        page: counterPage,
        ...sortingValue,
      } as RequestBodyType;

      if (requestType === 'ESSTaskRequest') {
        bodyRequest = {
          ...bodyRequest,
          services,
        };
      }

      let requestsList = requestsDataList;

      // clear previous data list if filter or search value applied first time
      if (clearRequestDataList) {
        setRequestsDataList([]);
        requestsList = [];
      }

      getRequests(bodyRequest)
        .unwrap()
        .then((response) => {
          setListRequests(response);
          if (response?.content) {
            const array = [...requestsList, ...response.content];
            let filtredDuplicates = [] as RequestResponseSearchListTypes[];

            if (
              requestType === RequestTypeEnum.LEAVE ||
              requestType === RequestTypeEnum.PASSPORT ||
              requestType === RequestTypeEnum.ATTENDANCE ||
              requestType === RequestTypeEnum.RESUMPTION ||
              requestType === RequestTypeEnum.OVERSEAS ||
              requestType === RequestTypeEnum.SALARY
            ) {
              filtredDuplicates = filterDuplicateObjects(
                array as HrmRequestFormResponse[],
                'requestId'
              );
            }

            if (requestType === RequestTypeEnum.ATTENDANCE_PROFILE) {
              filtredDuplicates = response?.content;
            }

            if (requestType === RequestTypeEnum.MR_TASK_REQUEST) {
              filtredDuplicates = filterDuplicateObjects(
                array as ApprovalsTasksRequisitionResponse[],
                'reqId'
              );
            }

            if (requestType === RequestTypeEnum.ESS_TASK_REQUEST) {
              filtredDuplicates = filterDuplicateObjects(
                array as ApprovalsTasksResponse[],
                'requestId'
              );
            }

            if (requestType === RequestTypeEnum.IT_REQUISITION) {
              filtredDuplicates = filterDuplicateObjects(
                array as ITRequestFormResponse[],
                'requestId'
              );
            }

            if (requestType === RequestTypeEnum.RESIGNATION) {
              filtredDuplicates = filterDuplicateObjects(
                array as ResignationRequestFormResponse[],
                'requestId'
              );
            }

            if (requestType === RequestTypeEnum.MATERIAL) {
              filtredDuplicates = filterDuplicateObjects(
                array as MaterialRequisitionFormResponse[],
                'requisitionId'
              );
            }

            if (requestType === RequestTypeEnum.PURCHASE) {
              filtredDuplicates = filterDuplicateObjects(
                array as PurchaseFormResponse[],
                'reqId'
              );
            }

            if (requestType === RequestTypeEnum.TENDER) {
              filtredDuplicates = filterDuplicateObjects(
                array as TenderDTO[],
                'id'
              );
            }
            setRequestsDataList(filtredDuplicates);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
      dispatch(eventMessageActions.sendEventType('NONE'));
    },
    [requestsDataList, requestType, filterSettings, searchValue, sorting]
  );

  const fetchRequest = useCallback(
    (changedPage?: number, sortConfig?: SortConfig | null) => {
      if (isMobile) {
        handleLoadMore();
        return;
      }

      const pageIndex = Number.isFinite(changedPage)
        ? changedPage
        : currentPage;

      const sortingValue = sortConfig || sorting;

      let bodyRequest = {
        searchValue,
        filters,
        page: pageIndex,
        ...sortingValue,
      } as RequestBodyType;

      if (requestType === 'ESSTaskRequest') {
        bodyRequest = {
          ...bodyRequest,
          services,
        };
      }

      getRequests(bodyRequest)
        .unwrap()
        .then((response) => {
          if (!response.content?.length) {
            const latestAvailablePage = getLatestAvailablePage(
              response.totalElements,
              response.size
            );

            let bodyFetchRequest = {
              searchValue,
              filters,
              page: latestAvailablePage,
              ...sortingValue,
            } as RequestBodyType;

            if (requestType === 'ESSTaskRequest') {
              bodyFetchRequest = {
                ...bodyFetchRequest,
                services,
              };
            }

            setCurrentPage(latestAvailablePage);
            getRequests(bodyFetchRequest)
              .unwrap()
              .then((resp) => {
                setListRequests(resp);
              });
          } else {
            setListRequests(response);
          }
        })
        .catch(() => {});
      dispatch(eventMessageActions.sendEventType('NONE'));
    },
    [currentPage, sorting, filters, searchValue, isMobile]
  );

  const onChangeCurrentPage = (value: number): void => {
    setCurrentPage(value);
  };

  useEffect(() => {
    if (!isMobile && eventMessage === EventMessageTypes.CONFIRM_REFETCH) {
      fetchRequest();
    }

    if (isMobile && eventMessage === EventMessageTypes.CONFIRM_REFETCH) {
      handleLoadMore();
    }
  }, [eventMessage]);

  useEffect(() => {
    if (eventMessage === 'FILTER_RESET') {
      clearParams();
      dispatch(eventMessageActions.sendEventType('REFETCH'));
      dispatch(filterOptionsActions.setFilterOptions(null));
    }
  }, [eventMessage]);

  useEffect(() => {
    if (!isRequestFormPage) {
      if (isRequestFormPagePrev.current) {
        setCurrentPage(TABLE_PAGE_DEFAULT);
      }

      if (!isMobile && !listRequests) {
        fetchRequest();
      }

      if (isMobile && !listRequests) {
        handleLoadMore();
      }

      if (
        !isMobile &&
        (eventMessage === EventMessageTypes.REFETCH ||
          eventMessage === EventMessageTypes.SEARCH ||
          eventMessage === EventMessageTypes.FILTER)
      ) {
        fetchRequest();
      }

      if (
        isMobile &&
        (eventMessage === EventMessageTypes.FILTER ||
          eventMessage === EventMessageTypes.SEARCH)
      ) {
        handleLoadMore(0, true);
      }

      if (isMobile && eventMessage === EventMessageTypes.REFETCH) {
        handleLoadMore();
      }
    }
  }, [
    isRequestFormPage,
    fetchRequest,
    filters,
    filterSettings,
    eventMessage,
    isMobile,
  ]);

  return {
    currentPage,
    fetchRequest,
    isSuccess,
    isLoading,
    listRequests,
    requestsDataList,
    handleLoadMore,
    onChangeCurrentPage,
  };
};

export default useRequestRefetcher;
